<section class="rule-details" aria-labelledby="rule-details-heading">
  <div class="section-heading">
    <h2 id="rule-details-heading">Rule Details</h2>
    @if (isAgedInventoryRule()) {
      <p>Define the vehicle criteria for this private aged inventory strategy.</p>
    } @else {
      <p>Define the rule eligibility criteria. All offer sets that meet the criteria will be impacted by the rule.</p>
    }
  </div>

  @if (isPercentageDiscount && !percentageDiscountEnabled) {
    <p role="status">Percentage discount rules are currently unavailable. This rule cannot be staged.</p>
  }
  @if (isAgedInventoryRule() && !agedInventoryEnabled) {
    <p role="status">Aged Inventory rules are currently unavailable. This rule cannot be staged.</p>
  }

  <form [formGroup]="ruleDetailsForm" novalidate>
    <div class="form-grid">
      <div class="rule-kind-fields" [class.has-discount-type]="isDiscountRule() && percentageDiscountEnabled">
        <div class="form-field">
          <label for="rule-type">Rule Type</label>
          <select id="rule-type" formControlName="ruleType">
            @for (option of ruleTypeOptions; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
            @if (agedInventoryEnabled) {
              <option [ngValue]="agedInventoryRuleType">Aged Inventory Rule</option>
            }
          </select>
        </div>

        @if (isDiscountRule() && percentageDiscountEnabled) {
          <div class="form-field">
            <label for="discount-type">Discount Type</label>
            <select id="discount-type" formControlName="discountType">
              @for (option of discountTypeOptions; track option.value) {
                <option [ngValue]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>
        }

        <div class="form-field">
          <label for="rule-privacy">Rule Privacy</label>
          <select id="rule-privacy" formControlName="privacy">
            @for (option of privacyOptions; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>

        <div class="form-field toggle-field">
          <input id="rule-status" type="checkbox" formControlName="status">
          <label for="rule-status">Rule is active</label>
        </div>
      </div>

      <div class="form-field">
        <label for="vehicle-type">Vehicle Type</label>
        <select id="vehicle-type" formControlName="vehicleType">
          @for (option of vehicleTypeOptions; track option.value) {
            <option [ngValue]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>

      <div class="form-field">
        <label for="rule-year">Year</label>
        <input id="rule-year" type="number" inputmode="numeric" formControlName="year" min="1">
        @if (ruleDetailsForm.controls.year.invalid && ruleDetailsForm.controls.year.touched) {
          <span class="field-error">Year is required and must be a positive whole number.</span>
        }
      </div>

      <div class="form-field">
        <label for="rule-make">Make</label>
        <select id="rule-make" formControlName="make">
          <option [ngValue]="null">Select make</option>
          @for (make of facets.makes; track make) {
            <option [ngValue]="make">{{ make }}</option>
          }
        </select>
        @if (ruleDetailsForm.controls.make.invalid && ruleDetailsForm.controls.make.touched) {
          <span class="field-error">Make is required.</span>
        }
      </div>

      <div class="form-field">
        <label for="rule-model">Model</label>
        <select id="rule-model" formControlName="model">
          <option [ngValue]="null">Select model</option>
          @for (model of facets.models; track model) {
            <option [ngValue]="model">{{ model }}</option>
          }
        </select>
        @if (ruleDetailsForm.controls.model.invalid && ruleDetailsForm.controls.model.touched) {
          <span class="field-error">Model is required.</span>
        }
      </div>

      <div class="form-field">
        <label for="rule-trim">Trim</label>
        <select id="rule-trim" formControlName="trim">
          <option [ngValue]="anyTrimValue">Any</option>
          @for (trim of facets.trims; track trim) {
            <option [ngValue]="trim">{{ trim }}</option>
          }
        </select>
        @if (ruleDetailsForm.controls.trim.value === anyTrimValue) {
          <span class="field-warning">This rule will apply to all trims unless specified</span>
        }
        @if (ruleDetailsForm.controls.trim.invalid && ruleDetailsForm.controls.trim.touched) {
          <span class="field-error">Trim is required.</span>
        }
      </div>

      <div class="form-field">
        <label for="vehicle-limit">Vehicle Limit</label>
        <input id="vehicle-limit" type="number" inputmode="numeric" formControlName="vehicleLimit" min="0" step="1">
        @if (ruleDetailsForm.controls.vehicleLimit.invalid && ruleDetailsForm.controls.vehicleLimit.touched) {
          <span class="field-error">Vehicle Limit must be a non-negative whole number.</span>
        }
      </div>
      <div class="form-field">
        <label for="minimum-price">Minimum Price <em>(optional)</em></label>
        <input id="minimum-price" type="number" inputmode="decimal" formControlName="minimumPrice" min="0" step="0.01"
               (input)="onMinimumPriceInput($event)">
        @if (ruleDetailsForm.controls.minimumPrice.invalid
          && (ruleDetailsForm.controls.minimumPrice.touched || ruleDetailsForm.controls.minimumPrice.hasError('badInput'))) {
          @if (isAgedInventoryRule()) {
            <span class="field-error">Enter a Minimum Price from 0 to 99,999,999.99 with up to two decimal places, or leave it blank.</span>
          } @else {
            <span class="field-error">Minimum Price must be a finite non-negative number.</span>
          }
        }
        @if (ruleDetailsForm.hasError('priceRange')) {
          <span class="field-error">Minimum Price must be less than or equal to Maximum Price.</span>
        }
      </div>
      <div class="form-field">
        <label for="maximum-price">Maximum Price</label>
        <input id="maximum-price" aria-describedby="price-overlap-error" [attr.aria-invalid]="rangeOverlapError !== ''" type="number" inputmode="decimal" formControlName="maximumPrice" min="0" step="0.01">
        @if (ruleDetailsForm.controls.maximumPrice.invalid && ruleDetailsForm.controls.maximumPrice.touched) {
          <span class="field-error">Maximum Price is required and must be a finite non-negative number.</span>
        }
        <span id="price-overlap-error" class="field-error" role="alert">{{ rangeOverlapError }}</span>
      </div>
      @if (isAgedInventoryRule() && agedInventoryEnabled) {
        <div class="aged-configuration-fields">
          <div class="form-field">
            <label for="aged-max-discount">Max Discount Value <em>(optional)</em></label>
            <input id="aged-max-discount" type="number" inputmode="decimal" formControlName="maxDiscountAmount"
                   min="0.01" [max]="maximumCurrencyAmount" step="0.01" aria-describedby="aged-cap-help aged-cap-error"
                   [attr.aria-invalid]="ruleDetailsForm.controls.maxDiscountAmount.invalid" (input)="onAgedCapInput($event)">
            <span id="aged-cap-help" class="field-help">Leave blank for no maximum discount. If entered, the cap must be greater than $0.</span>
            @if (ruleDetailsForm.controls.maxDiscountAmount.invalid
              && (ruleDetailsForm.controls.maxDiscountAmount.touched || ruleDetailsForm.controls.maxDiscountAmount.hasError('badInput'))) {
              <span id="aged-cap-error" class="field-error">Enter a cap from $0.01 to $99,999,999.99 with up to two decimal places, or leave it blank.</span>
            }
          </div>
          <div class="form-field">
            <label for="aged-discount-basis">Discount Basis</label>
            <select id="aged-discount-basis" formControlName="discountBasis" aria-describedby="aged-basis-help">
              <option value="selling_price">Selling Price</option>
              <option value="msrp">MSRP</option>
            </select>
            <span id="aged-basis-help" class="field-help">Applies to every Percentage bucket. Dollar buckets use their entered amount.</span>
          </div>
        </div>
      }
      @if (isDiscountRule()) {
        <div class="discount-fields" [class.has-basis]="isPercentageDiscount && percentageDiscountEnabled">
          <div class="form-field">
            <label for="dollar-discount">Discount Value</label>
            <div class="discount-value-input" [class.percentage-discount]="isPercentageDiscount">
              <input id="dollar-discount" type="number" inputmode="decimal" formControlName="dollarDiscount"
                     [min]="discountMinimum" [max]="discountMaximum" step="0.01"
                     aria-describedby="discount-value-help">
              @if (isPercentageDiscount) { <span aria-hidden="true">%</span> }
              <span id="discount-value-help">
                @if (isPercentageDiscount) {
                  Discount is applied to the current selling price, regardless of discount basis.
                } @else {
                  Enter the dollar discount amount.
                }
              </span>
            </div>
            @if (ruleDetailsForm.controls.dollarDiscount.invalid && ruleDetailsForm.controls.dollarDiscount.touched) {
              @if (isPercentageDiscount) {
                <span class="field-error">Enter a percentage from 0 to 100 with up to two decimal places.</span>
              } @else {
                <span class="field-error">Dollar Discount Amount must be greater than zero.</span>
              }
            }
          </div>
          @if (isPercentageDiscount && percentageDiscountEnabled) {
            <div class="form-field">
              <label for="discount-basis">Discount Basis</label>
              <select id="discount-basis" formControlName="discountBasis">
                <option value="selling_price">Selling Price</option>
                <option value="msrp">MSRP</option>
              </select>
            </div>
          }
        </div>
      }

    </div>
  </form>

  @if (canDiscover()) {
    <auto-ipop-rule-workflow-qualifying-vehicles
      [discoveryState]="discoveryState"
      [selectedSource]="selectedSource"
      [selectionEnabled]="!isReadOnly && isOfferSetRule() && !isEditMode"
      [criteriaPreview]="isAgedInventoryRule()"
      [pageSize]="pageSize"
      [maximumPriceSortDirection]="maximumPriceSortDirection"
      (sourceSelected)="onSourceSelected($event)"
      (pageChanged)="onPageChanged($event)"
      (maximumPriceSortDirectionChanged)="onMaximumPriceSortDirectionChanged($event)"
      (retryRequested)="onRetryRequested()">
    </auto-ipop-rule-workflow-qualifying-vehicles>
  } @else if (!isAgedInventoryRule() || agedInventoryEnabled) {
    <div class="discovery-validation" role="alert">
      <strong>Qualifying Vehicles are unavailable.</strong>
      <span>Provide a valid account and correct invalid Year or price range values to discover vehicles.</span>
    </div>
  }
</section>
